/**
 * Reseller (SMPP wholesale account) — domain layer.
 */

export type ResellerStatus = 'active' | 'pending' | 'suspended' | 'terminated';
export type ResellerTier = 'bronze' | 'silver' | 'gold' | 'platinum';
export type BillingModel = 'prepaid' | 'postpaid';

export interface ResellerListRow {
  id: string;
  name: string;
  system_id: string;
  status: ResellerStatus;
  tier_level: ResellerTier | string;
  billing_model: BillingModel | string;
  balance: string;
  created_at: string;
}

export interface ResellerDetail extends ResellerListRow {
  suspension_reason: string;
  price_per_segment: string;
  commission_percent: string;
  volume_discount_percent: string;
  volume_threshold: number;
  max_connections: number;
  rate_limit_per_second: number;
  daily_quota: number | null;
  monthly_quota: number | null;
  allowed_ips: string[];
  require_tls: boolean;
  credit_limit: string;
  low_balance_threshold: string;
  auto_recharge: boolean;
  auto_recharge_amount: string;
  auto_recharge_threshold: string;
  contact_email: string;
  contact_phone: string;
  webhook_url: string;
  updated_at: string;
  activated_at: string | null;
  suspended_at: string | null;
  terminated_at: string | null;
  active_sessions_count: number;
  today_message_count: number;
}

// Field names as /resellers/{id}/sessions/ returns them (the page read
// bound_at/unbound_at/bind_mode, which the API never sent)
export interface ResellerSession {
  id: string;
  client_ip: string;
  status: string;
  is_tls: boolean;
  connected_at: string;
  disconnected_at: string | null;
  messages_sent: number;
}

export interface ResellerTransaction {
  id: string;
  reseller: string;
  transaction_type: string;
  amount: string;
  balance_after: string;
  reason: string;   // the API's name for the description
  created_at: string;
}

export const canActivate = (r: { status: ResellerStatus }): boolean =>
  r.status === 'pending' || r.status === 'suspended';
export const canSuspend = (r: { status: ResellerStatus }): boolean =>
  r.status === 'active';
export const canTerminate = (r: { status: ResellerStatus }): boolean =>
  r.status !== 'terminated';
export const canCredit = (r: { status: ResellerStatus }): boolean =>
  r.status !== 'terminated';

/** POST /resellers/ body; system_id and password are generated server-side. */
export interface NewReseller {
  name: string;
  contact_email?: string;
  contact_phone?: string;
  tier_level: 'bronze' | 'silver' | 'gold' | 'partner';
  billing_model: 'prepaid' | 'postpaid';
  credit_limit?: string;
  max_connections: number;
  rate_limit_per_second: number;
  daily_quota: number;
  allowed_ips: string[];
}
